import { initializeApp, type FirebaseOptions } from "firebase/app";
import { getAuth, browserLocalPersistence, setPersistence } from "firebase/auth";
import {
  initializeFirestore, persistentLocalCache, persistentMultipleTabManager,
} from "firebase/firestore";
import { getFunctions } from "firebase/functions";
import { getStorage } from "firebase/storage";

/**
 * Ligação com o Firebase.
 *
 * As chaves vêm de variáveis de ambiente e NÃO são segredo — elas apenas
 * identificam o projeto. Quem protege os dados são as regras do Firestore
 * e do Storage, em `infra/`. O motivo de estarem em `.env` não é sigilo, é
 * poder apontar o mesmo código para produção, para o emulador e para um
 * projeto de teste sem editar arquivo.
 */
const config: FirebaseOptions = {
  apiKey: import.meta.env["VITE_FIREBASE_API_KEY"],
  authDomain: import.meta.env["VITE_FIREBASE_AUTH_DOMAIN"],
  projectId: import.meta.env["VITE_FIREBASE_PROJECT_ID"],
  storageBucket: import.meta.env["VITE_FIREBASE_STORAGE_BUCKET"],
  messagingSenderId: import.meta.env["VITE_FIREBASE_SENDER_ID"],
  appId: import.meta.env["VITE_FIREBASE_APP_ID"],
};

/** A região onde as funções vivem; sem isto o SDK procura em us-central1. */
export const FUNCTIONS_REGION = "southamerica-east1";

/**
 * O Nhomi e o GymEats dividem o mesmo projeto Firebase por ora, então o
 * Nhomi usa um banco Firestore NOMEADO em vez do `(default)`. É o que
 * mantém as duas bases separadas — coleções, regras e cotas — sem precisar
 * de um segundo projeto.
 */
export const DATABASE_ID = import.meta.env["VITE_FIRESTORE_DATABASE"] || "(default)";

export const configured = Boolean(config.apiKey && config.projectId);

export const app = initializeApp(config);

export const auth = getAuth(app);
setPersistence(auth, browserLocalPersistence).catch(() => {
  /* aba anônima: segue sem persistir */
});

/**
 * Cache em IndexedDB: o app abre com os dados da última visita, sem rede.
 *
 * `persistentMultipleTabManager`, e a diferença não é detalhe. O gerente
 * de aba ÚNICA deixa um só cliente possuir o IndexedDB; qualquer outro
 * fica sem sincronizar. E "outro cliente" não é caso raro aqui: o PWA
 * instalado e a aba do Chrome são a MESMA ORIGEM e dividem o mesmo
 * IndexedDB, então abrir o site com o app instalado já são dois.
 *
 * O sintoma é cruel de diagnosticar porque não parece problema de dado:
 * o segundo cliente abre, mostra o feed que estava em cache e nunca mais
 * recebe nada. Campo novo não aparece, escrita feita do outro lado não
 * chega, e tudo indica bug de tela. Foi assim que apareceu — as reações
 * funcionavam no navegador e não no app instalado.
 *
 * Com o gerente de múltiplas abas, um cliente é eleito dono da conexão e
 * os outros acompanham pelo próprio IndexedDB. É para isso que ele
 * existe.
 */
export const db = initializeFirestore(
  app,
  { localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }) },
  DATABASE_ID,
);

export const storage = getStorage(app);
export const functions = getFunctions(app, FUNCTIONS_REGION);
